import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Copy, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { key: 'product_name', required: true },
  { key: 'product_brand', required: true },
  { key: 'product_size', required: true },
  { key: 'product_color', required: true },
  { key: 'vendor_name', required: true },
  { key: 'category', required: true },
  { key: 'unit', required: true },
  { key: 'mrp', required: false, numeric: true },
  { key: 'vendor_discount_percent', required: false, numeric: true, percent: true },
  { key: 'gst_percent', required: false, numeric: true, percent: true },
  { key: 'margin_percent', required: false, numeric: true, percent: true },
  { key: 'input_price', required: false, numeric: true },
];

const HEADER_LINE = COLUMNS.map(c => c.key).join('\t');

// Mirror of backend calculate_pricing (routes/products.py).
function calcSelling({ mrp, vendor_discount_percent, gst_percent, margin_percent, input_price }) {
  const M = Number(mrp) || 0;
  const V = Number(vendor_discount_percent) || 0;
  const G = Number(gst_percent) || 0;
  const Mar = Number(margin_percent) || 0;
  const I = Number(input_price) || 0;
  if (M > 0) {
    const custDisc = Mar >= 100 ? 0 : 100 - ((100 - V) / (1 - Mar / 100));
    const selling = M * (1 - custDisc / 100) * (1 + G / 100);
    return Math.round(selling * 100) / 100;
  }
  if (I > 0 && Mar > 0) {
    return Math.round(I * (1 + Mar / 100) * 100) / 100;
  }
  return 0;
}

function parsePaste(text, vendorNameSet, existingByKey) {
  const lines = text.split(/\r?\n/).map(l => l.replace(/\s+$/, '')).filter(l => l.length > 0);
  if (!lines.length) return [];
  // Skip a header row if it looks like one
  let startIdx = 0;
  const firstCells = lines[0].split('\t').map(s => s.trim().toLowerCase());
  const knownHeaders = new Set(COLUMNS.map(c => c.key));
  if (firstCells.some(c => knownHeaders.has(c))) startIdx = 1;

  return lines.slice(startIdx).map((line, i) => {
    const cells = line.split('\t').map(s => s.trim());
    const row = {};
    COLUMNS.forEach((col, j) => { row[col.key] = cells[j] ?? ''; });
    const errors = [];

    // Required
    COLUMNS.forEach(c => {
      if (c.required && !row[c.key]) errors.push(`${c.key} required`);
    });
    // Numeric
    COLUMNS.forEach(c => {
      if (c.numeric && row[c.key] !== '') {
        const n = Number(row[c.key]);
        if (Number.isNaN(n)) errors.push(`${c.key} not numeric`);
        else if (n < 0) errors.push(`${c.key} negative`);
        else if (c.percent && n > 100) errors.push(`${c.key} > 100`);
      }
    });
    // Vendor exists?
    if (row.vendor_name && !vendorNameSet.has(row.vendor_name.toLowerCase())) {
      errors.push(`vendor '${row.vendor_name}' not found`);
    }

    const natKey = [row.product_name, row.product_brand, row.product_size, row.product_color, (row.vendor_name || '').toLowerCase()].join('|').toLowerCase();
    const existing = existingByKey.get(natKey);
    const status = errors.length ? 'error' : (existing ? 'update' : 'new');

    // Preview = exactly what backend will save: blank numeric cells become 0
    // (override existing). No merging with existing values.
    const sellingPreview = errors.length ? null : calcSelling(row);

    return { idx: i + 1, row, errors, status, sellingPreview, existing };
  });
}

const STATUS_PILL = {
  new: 'bg-lime-100 text-lime-800',
  update: 'bg-amber-100 text-amber-800',
  error: 'bg-red-100 text-red-800',
};

const fmtNum = (v) => {
  const n = Number(v);
  if (!v && v !== 0) return '';
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

// Renders one preview cell with old → new diff for UPDATE rows.
// - NEW row: just shows the pasted value
// - UPDATE row, blank cell or same as existing: shows existing value in muted grey
// - UPDATE row, value changed: shows new in amber-700 + existing in tiny strikethrough
function DiffCell({ pasted, existing, isUpdate, numeric = false, align = 'left', maxW }) {
  const fmt = numeric ? fmtNum : (v) => (v == null ? '' : String(v));
  const alignCls = `text-${align}`;
  const numCls = numeric ? 'tabular-nums' : '';
  const styleProp = maxW ? { maxWidth: maxW } : {};

  // Effective value that will be saved:
  // - numeric blank → 0 (override semantics)
  // - text blank → '' (but text fields are validated as required, so always present)
  const effective = numeric
    ? ((pasted === '' || pasted == null) ? 0 : Number(pasted))
    : (pasted == null ? '' : pasted);

  if (!isUpdate) {
    return (
      <td className={`px-2 py-1 ${alignCls} ${numCls} truncate`} style={styleProp} title={fmt(effective)}>
        {numeric ? (effective ? fmt(effective) : <span className="text-stone-300">—</span>)
                 : (effective ? fmt(effective) : <span className="text-stone-300">—</span>)}
      </td>
    );
  }

  // UPDATE
  const same = numeric
    ? Number(effective) === Number(existing || 0)
    : String(effective).trim() === String(existing || '').trim();

  if (same) {
    return (
      <td className={`px-2 py-1 ${alignCls} ${numCls} text-stone-400 truncate`} style={styleProp} title={`unchanged · ${fmt(effective)}`}>
        {effective || effective === 0 ? (numeric && !effective ? <span className="text-stone-300">0</span> : fmt(effective)) : <span className="text-stone-300">—</span>}
      </td>
    );
  }
  return (
    <td className={`px-2 py-1 ${alignCls} ${numCls}`} style={styleProp}>
      <div className="text-amber-700 font-semibold truncate" title={`new: ${fmt(effective)}`}>
        {numeric && !effective ? '0' : fmt(effective)}
      </div>
      <div className="text-[9px] text-stone-400 line-through truncate" title={`was: ${fmt(existing)}`}>
        {existing != null && existing !== '' ? fmt(existing) : '—'}
      </div>
    </td>
  );
}

export default function BulkProductImport({ open, onOpenChange, vendors, products, onImported }) {
  const [pasteText, setPasteText] = useState('');
  const [mode, setMode] = useState('create'); // 'create' | 'upsert'
  const [headerCopied, setHeaderCopied] = useState(false);
  const [importing, setImporting] = useState(false);

  const vendorNameSet = useMemo(() => new Set((vendors || []).map(v => (v.vendor_name || '').toLowerCase())), [vendors]);
  const existingByKey = useMemo(() => {
    const vid2name = {};
    (vendors || []).forEach(v => { vid2name[v.vendor_id] = v.vendor_name; });
    const map = new Map();
    (products || []).forEach(p => {
      const vName = vid2name[p.vendor_id] || '';
      const k = [p.product_name, p.product_brand, p.product_size, p.product_color, vName.toLowerCase()].join('|').toLowerCase();
      map.set(k, { ...p, vendor_name: vName });
    });
    return map;
  }, [products, vendors]);

  const parsed = useMemo(() => parsePaste(pasteText, vendorNameSet, existingByKey), [pasteText, vendorNameSet, existingByKey]);

  const counts = useMemo(() => {
    const c = { new: 0, update: 0, error: 0 };
    parsed.forEach(r => { c[r.status] += 1; });
    return c;
  }, [parsed]);

  const handleOpenChange = (next) => {
    if (!next) { setPasteText(''); setMode('create'); setHeaderCopied(false); }
    onOpenChange(next);
  };

  const copyHeader = async () => {
    try {
      await navigator.clipboard.writeText(HEADER_LINE);
      setHeaderCopied(true);
      setTimeout(() => setHeaderCopied(false), 1500);
    } catch {
      toast.error('Copy failed — please copy manually');
    }
  };

  const canSubmit = parsed.length > 0 && counts.error === 0 && (mode === 'upsert' ? true : counts.update === 0) && !importing;

  const handleSubmit = async () => {
    setImporting(true);
    try {
      const rows = parsed.filter(r => r.status !== 'error').map(r => r.row);
      const res = await axios.post(`${BACKEND_URL}/api/products/bulk`, { mode, rows }, { withCredentials: true });
      const { created, updated, errors } = res.data;
      if (errors && errors.length) {
        toast.warning(`Imported with ${errors.length} server-side error(s). ${created} created · ${updated} updated.`);
      } else {
        toast.success(`${created} created · ${updated} updated`);
      }
      onImported?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Products</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1 — Header */}
          <div className="border border-stone-200 rounded-md p-3 bg-stone-50">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-stone-700">Step 1 — Copy this header row into the first row of your sheet</Label>
              <Button
                data-testid="bulk-copy-header"
                type="button"
                variant="outline"
                size="sm"
                onClick={copyHeader}
                className="h-7 text-xs"
              >
                {headerCopied ? <><Check className="w-3 h-3 mr-1 text-lime-600" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy header</>}
              </Button>
            </div>
            <pre className="mt-2 text-[11px] overflow-x-auto whitespace-pre bg-white border border-stone-200 rounded p-2 text-stone-700">{HEADER_LINE.replace(/\t/g, '  |  ')}</pre>
            <p className="text-[10px] text-stone-500 mt-1">
              Required: product_name, product_brand, product_size, product_color, vendor_name, category, unit. Optional numeric: mrp, vendor_discount_percent, gst_percent, margin_percent, input_price.
              <br />
              <strong className="text-amber-700">Update mode: blank numeric cells overwrite existing values with 0.</strong> Copy-paste all relevant columns from your sheet to avoid accidentally zeroing prices. The preview highlights every change.
            </p>
          </div>

          {/* Step 2 — Paste */}
          <div>
            <Label className="text-xs font-semibold text-stone-700">Step 2 — Paste rows from Excel / Google Sheets (Ctrl+V)</Label>
            <textarea
              data-testid="bulk-paste-textarea"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Paste tab-separated rows here…"
              className="w-full h-32 mt-1 text-[11px] font-mono border border-stone-300 rounded p-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>

          {/* Preview */}
          {parsed.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-stone-700">Preview ({parsed.length} rows)</Label>
                <div className="flex flex-wrap gap-2 text-xs items-center">
                  <span className="text-[10px] text-stone-500 mr-1">Legend:</span>
                  <span className="text-[10px] text-amber-700 font-semibold">new value</span>
                  <span className="text-[10px] text-stone-400 line-through">was</span>
                  <span className="text-[10px] text-stone-400">unchanged</span>
                  <span className="mx-1 text-stone-300">|</span>
                  <span data-testid="bulk-count-new" className={`px-2 py-0.5 rounded ${STATUS_PILL.new}`}>{counts.new} new</span>
                  <span data-testid="bulk-count-update" className={`px-2 py-0.5 rounded ${STATUS_PILL.update}`}>{counts.update} update</span>
                  <span data-testid="bulk-count-error" className={`px-2 py-0.5 rounded ${STATUS_PILL.error}`}>{counts.error} error</span>
                </div>
              </div>
              <div className="border border-stone-200 rounded-md overflow-auto max-h-[320px]">
                <table className="text-[11px] whitespace-nowrap">
                  <thead className="bg-stone-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold text-stone-700">#</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Status</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Name</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Brand</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Size</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Color</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Vendor</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Category</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Unit</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-stone-700">MRP</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-stone-700">VDisc%</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-stone-700">GST%</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-stone-700">Margin%</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-stone-700">Input</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-lime-700">Selling →</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map(({ idx, row, errors, status, sellingPreview, existing }) => {
                      const isUpdate = status === 'update';
                      const ex = existing || {};
                      return (
                      <tr key={idx} data-testid={`bulk-row-${idx}`} className="border-t border-stone-100 hover:bg-stone-50 align-top">
                        <td className="px-2 py-1 text-stone-500">{idx}</td>
                        <td className="px-2 py-1">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold ${STATUS_PILL[status]}`}>{status}</span>
                        </td>
                        <DiffCell pasted={row.product_name} existing={ex.product_name} isUpdate={isUpdate} maxW="200px" />
                        <DiffCell pasted={row.product_brand} existing={ex.product_brand} isUpdate={isUpdate} maxW="140px" />
                        <DiffCell pasted={row.product_size} existing={ex.product_size} isUpdate={isUpdate} maxW="100px" />
                        <DiffCell pasted={row.product_color} existing={ex.product_color} isUpdate={isUpdate} maxW="100px" />
                        <DiffCell pasted={row.vendor_name} existing={ex.vendor_name} isUpdate={isUpdate} maxW="140px" />
                        <DiffCell pasted={row.category} existing={ex.category} isUpdate={isUpdate} maxW="120px" />
                        <DiffCell pasted={row.unit} existing={ex.unit} isUpdate={isUpdate} maxW="80px" />
                        <DiffCell pasted={row.mrp} existing={ex.mrp} isUpdate={isUpdate} numeric align="right" />
                        <DiffCell pasted={row.vendor_discount_percent} existing={ex.vendor_discount_percent} isUpdate={isUpdate} numeric align="right" />
                        <DiffCell pasted={row.gst_percent} existing={ex.gst_percent} isUpdate={isUpdate} numeric align="right" />
                        <DiffCell pasted={row.margin_percent} existing={ex.margin_percent} isUpdate={isUpdate} numeric align="right" />
                        <DiffCell pasted={row.input_price} existing={ex.input_price} isUpdate={isUpdate} numeric align="right" />
                        <td className="px-2 py-1 text-right tabular-nums" data-testid={`bulk-selling-${idx}`}>
                          {sellingPreview === null || sellingPreview === undefined ? (
                            <span className="text-stone-300">—</span>
                          ) : (() => {
                            const existSelling = ex.selling_price;
                            const newSelling = sellingPreview;
                            const samePrice = isUpdate && Math.round(Number(existSelling || 0) * 100) === Math.round(newSelling * 100);
                            if (!isUpdate) {
                              return <span className="font-semibold text-lime-700">{newSelling > 0 ? fmtNum(newSelling) : '—'}</span>;
                            }
                            if (samePrice) {
                              return <span className="text-stone-400" title="unchanged">{fmtNum(existSelling)}</span>;
                            }
                            return (
                              <div>
                                <div className="text-lime-700 font-semibold" title={`new: ${fmtNum(newSelling)}`}>{fmtNum(newSelling)}</div>
                                <div className="text-[9px] text-stone-400 line-through" title={`was: ${fmtNum(existSelling)}`}>{fmtNum(existSelling)}</div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-2 py-1 text-red-700 text-[10px] whitespace-normal min-w-[160px] max-w-[260px]">{errors.length ? errors.join('; ') : ''}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Mode */}
          {parsed.length > 0 && counts.update > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-stone-800 mb-2">
                  {counts.update} row(s) match existing products (same name + brand + size + color + vendor).
                  Choose how to handle them:
                </p>
                <div className="flex gap-3 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      data-testid="bulk-mode-create"
                      type="radio"
                      name="bulk-mode"
                      value="create"
                      checked={mode === 'create'}
                      onChange={() => setMode('create')}
                    />
                    Skip duplicates (Create only)
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      data-testid="bulk-mode-upsert"
                      type="radio"
                      name="bulk-mode"
                      value="upsert"
                      checked={mode === 'upsert'}
                      onChange={() => setMode('upsert')}
                    />
                    Update existing rows too
                  </label>
                </div>
                {mode === 'create' && (
                  <p className="text-[10px] text-amber-700 mt-1">
                    In &quot;Create only&quot; mode, duplicates are blocked — switch to &quot;Update existing&quot; to import them.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-2 border-t border-stone-200 pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="bulk-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="bulk-submit-btn"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="bg-amber-700 hover:bg-amber-800 text-white disabled:opacity-50"
            >
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Import {mode === 'upsert' ? counts.new + counts.update : counts.new} product{(mode === 'upsert' ? counts.new + counts.update : counts.new) === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
